// Lightweight analytics wrapper around PostHog.
// No-op when VITE_POSTHOG_KEY is not set — keeps dev/local quiet and lets
// the project ship without analytics if the user hasn't created an account yet.
//
// Usage:
//   import { initAnalytics, track, identify, reset } from './lib/analytics.js';
//   initAnalytics();         // once at app start
//   identify(uid, { email });// after sign-in
//   track('deck_created', { deck_id });
//   reset();                 // after sign-out

let posthogPromise = null;
let enabled = false;

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

export function initAnalytics() {
  if (!KEY) return;
  if (posthogPromise) return;
  posthogPromise = import('posthog-js').then((mod) => {
    const posthog = mod.default || mod.posthog || mod;
    posthog.init(KEY, {
      api_host: HOST,
      // Don't capture pageviews automatically — the app is a SPA, we'll track explicitly
      capture_pageview: false,
      // Respect Do Not Track by default
      respect_dnt: true,
      person_profiles: 'identified_only',
      // Error tracking: uncaught errors + unhandled rejections, with stacks.
      capture_exceptions: true,
    });
    posthog.register({ build: __BUILD_ID__ });
    enabled = true;
    return posthog;
  }).catch((err) => {
    console.warn('PostHog failed to load:', err);
    return null;
  });
}

export async function track(event, props = {}) {
  if (!KEY) return;
  const posthog = await posthogPromise;
  if (!posthog || !enabled) return;
  try {
    posthog.capture(event, props);
  } catch (err) {
    // Analytics must never break the app
    console.warn('analytics.track failed:', err);
  }
}

export async function identify(userId, traits = {}) {
  if (!KEY || !userId) return;
  const posthog = await posthogPromise;
  if (!posthog || !enabled) return;
  try {
    posthog.identify(userId, traits);
  } catch (err) {
    console.warn('analytics.identify failed:', err);
  }
}

export async function reset() {
  if (!KEY) return;
  const posthog = await posthogPromise;
  if (!posthog || !enabled) return;
  try {
    posthog.reset();
  } catch (err) {
    console.warn('analytics.reset failed:', err);
  }
}

// Errors the app catches (so autocapture never sees them) but that we need to
// know about from the field: failed go-live, failed votes, publish failures,
// Firestore permission denials. Never throws.
export async function reportError(err, context = {}) {
  if (!KEY) return;
  const posthog = await posthogPromise;
  if (!posthog || !enabled) return;
  try {
    const error = err instanceof Error ? err : new Error(String(err?.message || err));
    posthog.captureException(error, { ...context, error_code: err?.code || undefined });
  } catch (e) {
    console.warn('analytics.reportError failed:', e);
  }
}

// Attach properties to every later event/exception (route, role, session code).
export async function setErrorContext(props) {
  if (!KEY) return;
  const posthog = await posthogPromise;
  if (!posthog || !enabled) return;
  try { posthog.register(props); } catch {}
}

// Convenience: track a route change. Call from the router.
export async function trackPageview(pathname) {
  return track('$pageview', { $pathname: pathname });
}
